import { useState, useEffect, useCallback, useMemo, useRef } from 'react'

import { useMediaQuery, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CallBackProps, Step, EVENTS, STATUS, ACTIONS, ORIGIN } from 'react-joyride'
import { useLocation } from 'react-router-dom'

import { useAppSelector } from '../../../store/hooks'
import {
  getActiveTourStateFromStorage,
  setActiveTourStateInStorage,
  clearActiveTourStateFromStorage,
  markTourCompletedInStorage,
  isTourCompletedInStorage,
} from '../helpers/localStorageTour'
import { getTourSteps } from '../tourSteps'
import { TourType } from '../types'

import { usePageScrollLock } from './usePageScrollLock'

// ✅ small helper
const resolveTarget = (target: Step['target']): HTMLElement | null => {
  if (typeof target === 'string') return document.querySelector(target)
  if (typeof target === 'function') {
    try {
      return (target as () => HTMLElement | null)()
    } catch {
      return null
    }
  }
  return null
}

export function useJoyrideLogic() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const theme = useTheme()
  const isMobileSideMenu = useMediaQuery(theme.breakpoints.down('lg'))

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const hasAccount = Boolean(currentAccount?.id)

  const [steps, setSteps] = useState<Step[]>([])
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [tour, setTour] = useState<TourType | null>(null)
  const launched = useRef<Record<TourType, boolean>>({
    settings: false,
    reports: false,
    dashboard: false,
    profile: false,
  })

  usePageScrollLock(run)

  const tours = useMemo(
    () => getTourSteps(t, isMobileSideMenu, isMobile),
    [t, isMobileSideMenu, isMobile],
  )

  const availableTours = useMemo(() => {
    return (Object.keys(tours) as TourType[]).map((type) => ({
      type,
      matches: type === 'dashboard' ? pathname === '/dashboard' : pathname.startsWith(`/${type}`),
      steps: tours[type] ?? [],
    }))
  }, [pathname, tours])

  // --- start / stop helpers ---
  const startTour = useCallback(
    (type: TourType, step = 0) => {
      const found = availableTours.find((t) => t.type === type)
      if (!found?.steps.length) return

      const el = resolveTarget(found.steps[step]?.target)
      if (!el) return

      setSteps(found.steps)
      setStepIndex(step)
      setTour(type)
      setRun(true)
      launched.current[type] = true
      setActiveTourStateInStorage(type, step)
    },
    [availableTours],
  )

  const stopTour = useCallback((type?: TourType) => {
    if (type) markTourCompletedInStorage(type)
    clearActiveTourStateFromStorage()
    setRun(false)
    setSteps([])
    setStepIndex(0)
    setTour(null)
  }, [])

  // --- joyride event handler ---
  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { type, status, index, action, origin, step } = data

      // step trigger
      if (type === EVENTS.STEP_BEFORE) {
        const open = step?.data?.open
        if (typeof open === 'string') document.querySelector<HTMLElement>(open)?.click()
        else if (typeof open === 'function')
          try {
            open()
          } catch {}
      }

      if (action === ACTIONS.CLOSE && origin === ORIGIN.KEYBOARD) return setRun(false)
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED)
        return stopTour(tour || undefined)

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const next = index + (action === ACTIONS.PREV ? -1 : 1)
        if (next < 0 || next >= steps.length) return stopTour(tour || undefined)
        setStepIndex(next)
        if (tour) setActiveTourStateInStorage(tour, next)
      }
    },
    [steps, tour, stopTour],
  )

  // --- restore tour on reload ---
  useEffect(() => {
    if (!hasAccount) return
    const saved = getActiveTourStateFromStorage()
    if (!saved?.type) return
    if (isTourCompletedInStorage(saved.type)) return

    const tour = availableTours.find((t) => t.type === saved.type && t.matches)
    if (!tour) return

    const target = tour.steps[saved.step ?? 0]?.target
    const el = resolveTarget(target)

    if (el) startTour(tour.type, saved.step ?? 0)
    else
      setTimeout(() => {
        const retry = resolveTarget(target)
        retry ? startTour(tour.type, saved.step ?? 0) : clearActiveTourStateFromStorage()
      }, 1000)
  }, [availableTours, hasAccount, startTour])

  // --- auto-start first eligible tour ---
  useEffect(() => {
    if (!hasAccount || run || tour) return
    const next = availableTours.find(
      (t) => t.matches && !isTourCompletedInStorage(t.type) && !launched.current[t.type],
    )
    if (!next) return
    const id = setTimeout(() => startTour(next.type), 500)
    return () => clearTimeout(id)
  }, [hasAccount, run, tour, availableTours, startTour])

  return { run, steps, stepIndex, handleJoyrideCallback, startTour }
}
